import {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { CustomAlert } from 'components/AlertsCollection';
import { StudyReadyEvent } from 'helper/flux/diffStudiesStatus';
import { readyNoticeMessages } from 'helper/flux/myStudiesAutoRefresh';

/** Quanto cada aviso passageiro fica na tela (`showToast` do protótipo, .dc.html:604). */
export const ACTION_SUCCESS_DURATION_MS = 3800;

/** Erro ou aviso de uma ação, que fica na tela até ser fechado. */
export interface ActionAlert {
  /** Muda a cada aviso, mesmo com o texto igual: o alerta é montado e anunciado de novo. */
  key: number;
  message: string;
  variant: 'danger' | 'warning';
}

/** Item da fila: o `id` é só da fila, para dois avisos de mesmo texto serem dois avisos. */
interface QueuedNotice {
  id: number;
  message: string;
}

const EMPTY_QUEUE: readonly QueuedNotice[] = [];

export interface UseActionFeedbackResult {
  /** Texto do aviso passageiro da vez (`Toast` do DS); vazio sem aviso. */
  successMessage: string;
  /** `onClose` do `Toast`: tira o aviso da vez e passa ao próximo da fila. */
  clearSuccess: () => void;
  /** Erro ou aviso persistente (`Snackbar` do DS acima da lista), ou `null`. */
  alert: ActionAlert | null;
  /** Fecha o alerta. */
  dismissAlert: () => void;
  /** `onFeedback` da coluna de ações: reparte o resultado pela variante. */
  showFeedback: (feedback: CustomAlert) => void;
  /**
   * Avisos de estudo pronto de um ciclo do polling (#2004): "<NOME> está pronto para download.",
   * no fim da fila, com no máximo 3 por lote e um resumo dos outros. Ignora a chave já vista.
   */
  showReadyNotices: (events: readonly StudyReadyEvent[]) => void;
}

/**
 * Resultado das ações da aba Meus Estudos (excluir, cancelar e reenviar; o baixar mostra o
 * progresso no card de download) num aviso único da tela, sem descartar a variante:
 * - sucesso: aviso passageiro (`Toast`), que some sozinho, como no protótipo;
 * - erro (`danger`) e aviso (`warning`, ex.: cancelar recusado): alerta que fica até ser fechado.
 *   Um erro que some sozinho seria a única fonte da informação (WCAG 2.2.1). Variante
 *   desconhecida conta como erro.
 *
 * Um sucesso não apaga o alerta: ele pode ser de outro estudo.
 *
 * Fila única dos avisos passageiros (#2004): o sucesso das ações e o aviso de estudo pronto
 * dividem o MESMO `Toast` (em `bottom-center`; o `DownloadProgressCard` ocupa o canto inferior
 * direito), um por vez, `ACTION_SUCCESS_DURATION_MS` cada. Dois toasts nunca ficam sobrepostos.
 * - A ação do usuário tem prioridade: o sucesso entra no lugar do aviso da vez, na hora. Ele
 *   interrompe um aviso de pronto (que não volta) e substitui o sucesso anterior, como antes da
 *   #2004. Os avisos seguintes da fila esperam por ele.
 * - Os prontos entram no fim, em ordem (2 no mesmo ciclo entram em fila), até 3 por lote; os
 *   outros do lote viram um aviso de resumo (`readyNoticeMessages`).
 * - Chave de pronto repetida, no mesmo lote ou depois, é ignorada. É uma defesa extra: quem
 *   garante cada chave uma vez é o `announcedRef` do `useMyStudiesStatusPolling`.
 * - A cada troca de aviso, `successMessage` fica vazio por um render: o `Toast` só reinicia a
 *   contagem quando o texto muda (dois avisos de mesmo texto ficariam presos), e o vazio faz o
 *   leitor de tela anunciar o novo texto.
 *
 * @returns O estado dos dois avisos e as ações, estáveis entre renders.
 */
export default function useActionFeedback(): UseActionFeedbackResult {
  const [queue, setQueue] = useState<readonly QueuedNotice[]>(EMPTY_QUEUE);
  // Um render com o aviso vazio a cada troca do aviso da vez (ver acima)
  const [betweenNotices, setBetweenNotices] = useState(false);
  const [alert, setAlert] = useState<ActionAlert | null>(null);
  const lastAlertKey = useRef(0);
  const lastNoticeId = useRef(0);
  const readyKeys = useRef(new Set<string>());

  useEffect(() => {
    if (betweenNotices) setBetweenNotices(false);
  }, [betweenNotices]);

  const toNotice = useCallback((message: string): QueuedNotice => {
    lastNoticeId.current += 1;
    return { id: lastNoticeId.current, message };
  }, []);

  const showFeedback = useCallback(({ message, variant }: CustomAlert) => {
    if (message.trim() === '') return;
    if (variant === 'success') {
      // Prioridade da ação: no lugar do aviso da vez (pronto ou sucesso), na hora
      const notice = toNotice(message);
      setQueue((current) => [notice, ...current.slice(1)]);
      setBetweenNotices(true);
      return;
    }
    lastAlertKey.current += 1;
    setAlert({
      key: lastAlertKey.current,
      message,
      variant: variant === 'warning' ? 'warning' : 'danger',
    });
  }, [toNotice]);

  const showReadyNotices = useCallback((events: readonly StudyReadyEvent[]) => {
    const fresh = events.filter(({ key }) => {
      if (readyKeys.current.has(key)) return false;
      readyKeys.current.add(key);
      return true;
    });
    if (fresh.length === 0) return;
    const notices = readyNoticeMessages(fresh.map(({ name }) => name))
      .map(toNotice);
    setQueue((current) => [...current, ...notices]);
  }, [toNotice]);

  const clearSuccess = useCallback(() => {
    setQueue((current) => (current.length > 0 ? current.slice(1) : current));
    setBetweenNotices(true);
  }, []);
  const dismissAlert = useCallback(() => setAlert(null), []);

  const successMessage = betweenNotices ? '' : queue[0]?.message ?? '';

  return {
    successMessage, clearSuccess, alert, dismissAlert, showFeedback, showReadyNotices,
  };
}
